import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  AIContext,
  EventEmitter,
  EventEmitterWithIndex
} from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import { DisplayText } from '@superops/ui-design-system';
import { AIOnboardingHook } from '@nile/components/src/Components/AI/hooks/AIOnboardingHook';
import SwitchableStep from './SwitchableStep';
import { renderActionButtons } from './buttonRenderer';
import { FormattedMessage, injectIntl } from 'react-intl';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';

const AIOnboardingComponent: React.FC = () => {
  const aiOnboardingHook = AIOnboardingHook();
  const aiFeatureHook = AIFeaturesHook();

  const { currentStep, nextStep } = aiOnboardingHook;
  const [apiLoading, setApiLoading] = useState(false);
  const [activeCustomComponent, setActiveCustomComponent] = useState<any>(null);
  const [activeCustomComponentProps, setActiveCustomComponentProps] = useState<
    any
  >({});
  const [hiddenButtons, setHiddenButtons] = useState<Map<number, Set<number>>>(
    new Map()
  );
  const [showSwitchableStep, setShowSwitchableStep] = useState(false);
  const [showBubbleBeforeNextStep, setShowBubbleBeforeNextStep] = useState<
    Map<number, boolean>
  >(new Map());
  const [containerHeight, setContainerHeight] = useState<number | 'auto'>(
    'auto'
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const aiContext = useContext(AIContext);

  // Move useEffect to component level to avoid hook rule violations
  useEffect(() => {
    // Initial delay to show switchable step after 3 seconds
    const initialDelay = setTimeout(() => {
      setShowSwitchableStep(true);
    }, 2000);

    return () => clearTimeout(initialDelay);
  }, []);

  const updateHeight = () => {
    const element = containerRef.current;
    if (element) {
      const height = element.scrollHeight;
      setContainerHeight(height);
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        !currentStep && updateHeight();
      });

      resizeObserver.observe(containerRef.current);
      !currentStep && updateHeight();

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [currentStep, showSwitchableStep, activeCustomComponent, hiddenButtons]);

  // Auto-switching logic moved to SwitchableStep component

  if (!aiOnboardingHook) {
    return <div>No onboarding configuration found</div>;
  }

  const WelcomeStep: React.FC = () => {
    const config = aiOnboardingHook.getOnboardingConfig();
    if (!config) return null;
    const currentFeature = aiFeatureHook?.getAIFeatureData(
      aiOnboardingHook.currentFeature
    );
    const displayIconUrl = currentFeature?.displayIconUrl;

    return (
      <div className="flex col g10 mt-20 mb-20">
        <span className="onboarding-logo">
          {/* <SVGIcon name={config.featureLogo} size="xxl" /> */}
          <img className="w-60 h60px" src={displayIconUrl} alt="Feature Icon" />
        </span>

        <DisplayText element="h4" size="extraLarge" weight="Bold">
          <FormattedMessage id={config.title} />
        </DisplayText>
        <DisplayText element="p" size="large">
          <FormattedMessage id={config.description} />
        </DisplayText>
      </div>
    );
  };

  const completeOnboardingWrapper = () => {
    aiContext?.setShowOnboarding(false);
    aiOnboardingHook?.completeOnboarding();
  };

  const handleButtonAction = (buttonConfig, index, stepIndex = currentStep) => {
    if (buttonConfig.type === 'activator' && buttonConfig.featureName) {
      completeOnboardingWrapper();
    } else if (buttonConfig.type === 'custom') {
      // cm::todo refactor
      if (buttonConfig.featureName === 'SCRIPT_GENERATION') {
        completeOnboardingWrapper();

        setTimeout(() => {
          EventEmitter.dispatch('toggleScriptAIPopup');
        }, 300);
      }
      if (
        buttonConfig.featureName === 'PROMPT_BUILDER' ||
        buttonConfig.featureName === 'WORKLOG_NOTE_COMPOSE'
      ) {
        completeOnboardingWrapper();

        EventEmitterWithIndex.dispatch('insertTicketConversation', {
          content: '  '
        });
      }

      if (buttonConfig.component) {
        setActiveCustomComponent(() => buttonConfig.component);
        setActiveCustomComponentProps(buttonConfig.props || {});
      }
      setHiddenButtons((prev) => {
        const newMap = new Map(prev);
        const stepButtons = newMap.get(stepIndex) || new Set();
        stepButtons.add(index);
        newMap.set(stepIndex, stepButtons);
        return newMap;
      });
    } else if (buttonConfig.type === 'nextstep') {
      setHiddenButtons((prev) => {
        const newMap = new Map(prev);
        const stepButtons = newMap.get(stepIndex) || new Set();
        stepButtons.add(index);
        newMap.set(stepIndex, stepButtons);
        return newMap;
      });

      setTimeout(() => {
        const nextStepIndex = currentStep + 1;
        nextStep();
        setContainerHeight('auto');

        setShowBubbleBeforeNextStep((prev) => {
          const newMap = new Map(prev);
          newMap.set(nextStepIndex, true);
          return newMap;
        });
      }, 500);
    }
  };

  const ActiveCustomComponent: React.FC = () => {
    if (!activeCustomComponent) return <></>;

    const CustomComponent = activeCustomComponent;
    return (
      <div className="ai-explore-further-section">
        <CustomComponent {...activeCustomComponentProps} />
      </div>
    );
  };

  const CustomComponentSection: React.FC = () => {
    const [showCustomComponent, setShowCustomComponent] = useState(false);
    const config = aiOnboardingHook.getOnboardingConfig();

    const timerRef = React.useRef(null);

    const handleCustomComponentDisplay = () => {
      // timerRef.current = setTimeout(() => {
      setShowCustomComponent(true);
      // }, config.customComponentTimeoutDelay);
    };

    useEffect(() => {
      if (
        !config ||
        !config.customComponent ||
        !config.customComponentTimeoutDelay
      )
        return;

      handleCustomComponentDisplay();

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }, []);

    if (!config || !config.customComponent || !showCustomComponent) return null;

    const CustomComponent = config.customComponent;
    return (
      <div className="ai-custom-component-section">
        <CustomComponent />
      </div>
    );
  };

  const renderStepContent = (stepIndex: number) => {
    const config = aiOnboardingHook.getOnboardingConfig();
    const currentStepConfig = config?.customSteps?.[stepIndex];

    if (currentStepConfig?.type === 'feature-functionality') {
      const CustomComponent = currentStepConfig.customComponent;
      if (CustomComponent) {
        return (
          <div className="ai-feature-functionality-section">
            <CustomComponent {...(currentStepConfig.props || {})} />
            {renderActionButtons({
              stepIndex,
              config,
              hiddenButtons,
              onButtonAction: handleButtonAction,
              containerClassName: 'ai-step-buttons mt-20'
            })}
          </div>
        );
      }
      return null;
    } else if (currentStepConfig?.type === 'feature-showcase') {
      return (
        <SwitchableStep
          stepIndex={stepIndex}
          hiddenButtons={hiddenButtons}
          apiLoading={apiLoading}
          showSwitchableStep={showSwitchableStep}
          onboardingConfig={config}
          onButtonAction={handleButtonAction}
          onGifLoad={updateHeight}
        />
      );
    }

    return null;
  };

  return (
    <div
      ref={containerRef}
      className="ai-onboarding-container hide-scroll"
      style={{
        height: containerHeight === 'auto' ? 'auto' : `${containerHeight}px`,
        transition: 'height 0.3s ease-in-out',
        overflow: 'scroll'
      }}
    >
      <div style={{ minHeight: 'min-content' }}>
        <div className="ai-onboarding-header">
          <WelcomeStep />
        </div>
        <div className="ai-onboarding-body">
          {showSwitchableStep && (
            <div key={currentStep} className="">
              {renderStepContent(currentStep)}
            </div>
          )}
        </div>
        <ActiveCustomComponent />
        <CustomComponentSection />
      </div>
    </div>
  );
};

export default injectIntl(AIOnboardingComponent);
